/**
 * AiLookupPanel.tsx — In-app AI lookup panel for work-order line items.
 *
 * Renders a tabbed panel with:
 *   - Parts:           parts lookup via Hub
 *   - Recalls/TSBs:    NHTSA recalls + TSBs via Hub
 *   - Similar Fixes:   semantic fix-knowledge search via Hub
 *
 * Only rendered when the shop has AI parts/recalls lookup enabled
 * (useAiFeatureFlags, migration 195 — owner-controlled in AI settings).
 * The feature-flag gate lives in OptimizedWorkOrderDetail — this component
 * itself has no guard so it is independently testable.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Button, Input, Spin, Alert, List, Tag, Typography, Space, Card, Select, Modal, Result, Descriptions } from 'antd';
import { SearchOutlined, BulbOutlined, WarningOutlined, LockOutlined, SafetyOutlined, ShoppingCartOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useAiLookup } from '../../hooks/useAiLookup';
import type { AiPartsResult, AiRecallItem, AiTsbItem, AiFixHit, AiLicensedResult, PartWarranty } from '../../services/aiLookupService';
import { WarrantyEditorModal } from '../Parts/WarrantyEditorModal';
import PartOrdersDrawer, { type PartOrder } from './PartOrdersDrawer';
// Part-order routes live at /api/parts (NOT /api/admin) — use the /api-rooted client.
import partsApi from '../../services/partsApi';

const { Text, Paragraph } = Typography;

export interface AiLookupPanelProps {
  /** ID of the work-order line item to look up */
  lineItemId: number;
  /** Work-order ID (needed to create part orders) */
  workOrderId?: number;
  /** Concern text (pre-fills the parts search query) */
  concern?: string;
}

const AiLookupPanel: React.FC<AiLookupPanelProps> = ({ lineItemId, workOrderId, concern }) => {
  const [partQuery, setPartQuery] = useState<string>(concern ?? '');
  const [licensedVendor, setLicensedVendor] = useState<'alldata' | 'autozone_pro'>('alldata');
  const [licensedQuery, setLicensedQuery] = useState<string>(concern ?? '');
  const {
    parts,
    recalls,
    tsbs,
    similarFixes,
    licensed,
    licensedProvenance,
    lookupParts,
    lookupRecalls,
    lookupTsbs,
    lookupSimilarFixes,
    lookupLicensed,
  } = useAiLookup();

  // ── Budget-exceeded state (Gap 3 UI) ────────────────────────────────────────
  const [budgetExceeded, setBudgetExceeded] = useState<{
    spent_cents?: number;
    budget_cents?: number;
  } | null>(null);

  // ── Part order select modal (Gap 1 UI) ──────────────────────────────────────
  const [selectModal, setSelectModal] = useState<{
    open: boolean;
    opt: any | null;
    confirming: boolean;
    error: string | null;
  }>({ open: false, opt: null, confirming: false, error: null });

  // Track which part_option IDs have been selected (to show "Selected" state)
  const [selectedOptionIds, setSelectedOptionIds] = useState<Set<number>>(new Set());

  // ── PartOrdersDrawer (Gap 1 UI) ─────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [partOrders, setPartOrders] = useState<PartOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);

  const loadPartOrders = useCallback(async () => {
    if (!lineItemId) return;
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const res = await partsApi.get(`/parts/orders?lineItemId=${lineItemId}`);
      setPartOrders(res.data);
    } catch (err: any) {
      setOrdersError(err?.response?.data?.message ?? err?.message ?? 'Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  }, [lineItemId]);

  async function handleSelectOption(opt: any) {
    if (!workOrderId) return;
    setSelectModal({ open: true, opt, confirming: false, error: null });
  }

  async function confirmSelectOption() {
    const { opt } = selectModal;
    if (!opt || !workOrderId) return;
    setSelectModal((s) => ({ ...s, confirming: true, error: null }));
    try {
      await partsApi.post('/parts/orders', {
        workOrderId,
        lineItemId,
        partOptionId: opt.id,
      });
      setSelectedOptionIds((prev) => {
        const next = new Set(Array.from(prev));
        next.add(opt.id as number);
        return next;
      });
      setSelectModal({ open: false, opt: null, confirming: false, error: null });
      // Load orders so the "Open orders" link is useful
      await loadPartOrders();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to select option';
      setSelectModal((s) => ({ ...s, confirming: false, error: msg }));
    }
  }

  // Optimistic warranty overrides keyed by option id.
  // When a user saves a warranty edit we update this map so the chip reflects
  // the new value immediately without waiting for a fresh lookup.
  const [warrantyOverrides, setWarrantyOverrides] = useState<
    Record<number, { warranty: PartWarranty | null; overridden: boolean }>
  >({});

  // Reset overrides whenever a new parts lookup replaces the result set —
  // option IDs from a previous search must not bleed into a new search's options.
  useEffect(() => {
    setWarrantyOverrides({});
  }, [parts.data]);

  // Load any persisted part orders for this line item on mount / line change,
  // so an already-selected (or any existing) order has a visible "Open orders"
  // entry point — not only after an in-session Select click.
  useEffect(() => {
    loadPartOrders();
  }, [loadPartOrders]);

  const [warrantyEditState, setWarrantyEditState] = useState<{
    open: boolean;
    optionId: number | null;
    initial: PartWarranty | null;
  }>({ open: false, optionId: null, initial: null });

  /**
   * Renders a warranty chip for a part option.
   * Falls back to "warranty unknown" when no warranty data is present.
   */
  function renderWarrantyChip(opt: any) {
    const optId: number | undefined = opt.id;
    const override = optId != null ? warrantyOverrides[optId] : undefined;
    const w: PartWarranty | null = override !== undefined ? override.warranty : (opt.warranty ?? null);
    const overridden = override !== undefined ? override.overridden : !!opt.warranty_overridden_by_user_id;

    if (!w) {
      return <Tag>warranty unknown</Tag>;
    }
    const segments: string[] = [];
    if (w.months != null) segments.push(`${w.months} mo`);
    if (w.miles != null) segments.push(`${(w.miles / 1000).toFixed(0)}k mi`);
    segments.push(w.type);
    return (
      <Tag color="purple">
        {segments.join(' / ')}{overridden ? ' ✎' : ''}
      </Tag>
    );
  }

  // ── Parts tab ──────────────────────────────────────────────────────────────
  const partsResultRaw = parts.data as (AiPartsResult & { cached?: boolean; vendor_degraded?: boolean }) | null;
  const vendorDegraded = partsResultRaw?.vendor_degraded ?? false;
  const isCached = partsResultRaw?.cached ?? false;

  const PartsTab = (
    // @ts-ignore TS2746: React 19 types + TS 4.9.5 multi-child JSX compat — pre-existing
    <div style={{ padding: '8px 0' }}>
      {/* Budget-exceeded Result card (Gap 3) */}
      {budgetExceeded && (
        <Result
          status="warning"
          title="AI lookups paused — monthly budget exceeded"
          subTitle={
            budgetExceeded.spent_cents != null && budgetExceeded.budget_cents != null
              ? `$${(budgetExceeded.spent_cents / 100).toFixed(2)} of $${(budgetExceeded.budget_cents / 100).toFixed(2)} used this month. Contact owner to raise the budget or wait for the next billing cycle.`
              : 'Contact owner to raise the budget or wait for the next billing cycle.'
          }
          style={{ padding: '12px 0' }}
          data-testid="budget-exceeded-card"
        />
      )}

      {/* Degraded / cached result banner (Gap 4) */}
      {(vendorDegraded || isCached) && (
        <Alert
          type="warning"
          showIcon
          message="Showing cached result — vendor temporarily unavailable"
          description="The vendor returned an error. These results may be stale. The system will auto-recover when the vendor comes back online."
          style={{ marginBottom: 8 }}
          data-testid="vendor-degraded-banner"
        />
      )}

      {/* Open orders link (Gap 1) — show when an order was just created this
          session OR when persisted part orders already exist for this line. */}
      {(selectedOptionIds.size > 0 || partOrders.length > 0) && (
        <Alert
          type="success"
          showIcon
          message={
            <Space>
              <span>
                {selectedOptionIds.size > 0
                  ? 'Part order created.'
                  : `${partOrders.length} part order${partOrders.length === 1 ? '' : 's'} on this line.`}
              </span>
              <Button
                type="link"
                size="small"
                icon={<UnorderedListOutlined />}
                onClick={() => { setDrawerOpen(true); loadPartOrders(); }}
                style={{ padding: 0 }}
                data-testid="open-orders-link"
              >
                Open orders
              </Button>
            </Space>
          }
          style={{ marginBottom: 8 }}
        />
      )}

      <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
        <Input
          placeholder="e.g. front brake pads"
          value={partQuery}
          onChange={(e) => setPartQuery(e.target.value)}
          onPressEnter={() => lookupParts(lineItemId, partQuery)}
          data-testid="part-query-input"
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          loading={parts.loading}
          disabled={!partQuery.trim()}
          onClick={() => lookupParts(lineItemId, partQuery)}
          data-testid="parts-lookup-btn"
        >
          Look up
        </Button>
      </Space.Compact>

      {parts.error && (
        <Alert type="error" message={parts.error} style={{ marginBottom: 8 }} />
      )}

      {parts.loading && <Spin style={{ display: 'block', margin: '16px auto' }} />}

      {!parts.loading && parts.data && (() => {
        const result = parts.data as AiPartsResult;
        const options = result?.options ?? [];
        return options.length === 0 ? (
          <Text type="secondary">No parts found.</Text>
        ) : (
          <List
            size="small"
            dataSource={options}
            renderItem={(opt) => (
              <List.Item
                key={`${opt.brand}-${opt.part_number}`}
                extra={
                  opt.price_cents != null ? (
                    <Tag color="green">${(opt.price_cents / 100).toFixed(2)}</Tag>
                  ) : null
                }
              >
                <List.Item.Meta
                  title={<Text strong>{opt.brand} {opt.part_number}</Text>}
                  description={
                    <Space size="small" wrap>
                      {opt.fitment_status && (
                        <Tag color={opt.fitment_status === 'fits' ? 'blue' : 'orange'}>
                          {opt.fitment_status}
                        </Tag>
                      )}
                      {opt.oem_or_aftermarket && (
                        <Tag>{opt.oem_or_aftermarket}</Tag>
                      )}
                      {renderWarrantyChip(opt)}
                      {opt.id != null && (
                        <Button
                          size="small"
                          type="link"
                          style={{ padding: '0 4px' }}
                          onClick={() => {
                            // Prefer the local override if one exists — otherwise the modal
                            // would reset back to the stale server value on a re-edit.
                            const override = warrantyOverrides[opt.id as number];
                            const currentWarranty = override !== undefined
                              ? override.warranty
                              : ((opt.warranty as PartWarranty) ?? null);
                            setWarrantyEditState({
                              open: true,
                              optionId: opt.id as number,
                              initial: currentWarranty,
                            });
                          }}
                        >
                          Edit warranty
                        </Button>
                      )}
                      {/* Gap 1: Select button → confirmation modal → create part_order */}
                      {opt.id != null && workOrderId && (
                        selectedOptionIds.has(opt.id as number) ? (
                          <Tag color="success" data-testid={`selected-badge-${opt.id}`}>
                            Selected
                          </Tag>
                        ) : (
                          <Button
                            size="small"
                            type="primary"
                            icon={<ShoppingCartOutlined />}
                            onClick={() => handleSelectOption(opt)}
                            data-testid={`select-option-btn-${opt.id}`}
                          >
                            Select
                          </Button>
                        )
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        );
      })()}
    </div>
  );

  // ── Recalls / TSBs tab ─────────────────────────────────────────────────────
  const handleRecallsTsbsLookup = () => {
    lookupRecalls(lineItemId);
    lookupTsbs(lineItemId);
  };

  const RecallsTsbsTab = (
    <div style={{ padding: '8px 0' }}>
      <Button
        icon={<WarningOutlined />}
        loading={recalls.loading || tsbs.loading}
        onClick={handleRecallsTsbsLookup}
        style={{ marginBottom: 12 }}
        data-testid="recalls-tsbs-lookup-btn"
      >
        Fetch Recalls &amp; TSBs
      </Button>

      {(recalls.error || tsbs.error) && (
        <Alert type="error" message={recalls.error ?? tsbs.error} style={{ marginBottom: 8 }} />
      )}

      {(recalls.loading || tsbs.loading) && (
        <Spin style={{ display: 'block', margin: '16px auto' }} />
      )}

      {!recalls.loading && recalls.data && (recalls.data as AiRecallItem[]).length > 0 && (
        <>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Recalls</Text>
          <List
            size="small"
            dataSource={recalls.data as AiRecallItem[]}
            renderItem={(item) => (
              <List.Item key={item.component}>
                <List.Item.Meta
                  title={<><Tag color="red">RECALL</Tag> {item.component}</>}
                  description={
                    <>
                      <Paragraph style={{ margin: 0 }}>{item.summary}</Paragraph>
                      <Text type="secondary">Remedy: {item.remedy}</Text>
                    </>
                  }
                />
              </List.Item>
            )}
          />
        </>
      )}

      {!recalls.loading && recalls.data && (recalls.data as AiRecallItem[]).length === 0 && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>No recalls found for this vehicle.</Text>
      )}

      {!tsbs.loading && tsbs.data && (tsbs.data as AiTsbItem[]).length > 0 && (
        <>
          <Text strong style={{ display: 'block', marginTop: 12, marginBottom: 4 }}>Technical Service Bulletins</Text>
          <List
            size="small"
            dataSource={tsbs.data as AiTsbItem[]}
            renderItem={(item) => (
              <List.Item key={item.id}>
                <List.Item.Meta
                  title={<><Tag color="orange">TSB</Tag> {item.id}</>}
                  description={item.description}
                />
              </List.Item>
            )}
          />
        </>
      )}

      {!tsbs.loading && tsbs.data && (tsbs.data as AiTsbItem[]).length === 0 && (
        <Text type="secondary" style={{ display: 'block' }}>No TSBs found for this vehicle.</Text>
      )}
    </div>
  );

  // ── Similar Fixes tab ──────────────────────────────────────────────────────
  const SimilarFixesTab = (
    <div style={{ padding: '8px 0' }}>
      <Button
        icon={<BulbOutlined />}
        loading={similarFixes.loading}
        onClick={() => lookupSimilarFixes(lineItemId)}
        style={{ marginBottom: 12 }}
        data-testid="similar-fixes-lookup-btn"
      >
        Find Similar Fixes
      </Button>

      {similarFixes.error && (
        <Alert type="error" message={similarFixes.error} style={{ marginBottom: 8 }} />
      )}

      {similarFixes.loading && (
        <Spin style={{ display: 'block', margin: '16px auto' }} />
      )}

      {!similarFixes.loading && similarFixes.data && (similarFixes.data as AiFixHit[]).length === 0 && (
        <Text type="secondary">No similar fixes found yet.</Text>
      )}

      {!similarFixes.loading && similarFixes.data && (similarFixes.data as AiFixHit[]).map((hit, i) => (
        <Card
          key={i}
          size="small"
          style={{ marginBottom: 8 }}
          extra={<Tag color="blue">{Math.round(hit._score * 100)}% match</Tag>}
        >
          <Paragraph style={{ margin: '0 0 4px 0' }}>
            <Text strong>Symptom:</Text> {hit.symptom}
          </Paragraph>
          <Paragraph style={{ margin: '0 0 4px 0' }}>
            <Text strong>Cause:</Text> {hit.cause}
          </Paragraph>
          <Paragraph style={{ margin: '0 0 4px 0' }}>
            <Text strong>Correction:</Text> {hit.correction}
          </Paragraph>
          <Space size="small">
            <Tag>{hit.mileage_band}</Tag>
            {hit.dtc_codes && <Tag color="purple">{hit.dtc_codes}</Tag>}
            <Tag color={hit.outcome === 'fixed' ? 'green' : 'red'}>{hit.outcome}</Tag>
          </Space>
        </Card>
      ))}
    </div>
  );

  // ── Procedures / Specs tab (licensed, not shared) ─────────────────────────
  const ProceduresTab = (
    <div style={{ padding: '8px 0' }}>
      <Alert
        icon={<SafetyOutlined />}
        type="info"
        showIcon
        message="From your subscription — not shared"
        description="Data from RepairData / AutoZone Pro is from your own subscription. It is stored locally and never shared with the AI knowledge pool."
        style={{ marginBottom: 12, fontSize: 12 }}
        data-testid="licensed-privacy-note"
      />

      <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
        <Select
          value={licensedVendor}
          onChange={(v) => setLicensedVendor(v)}
          options={[
            { value: 'alldata', label: 'RepairData' },
            { value: 'autozone_pro', label: 'AutoZone Pro' },
          ]}
          style={{ width: 160 }}
          data-testid="licensed-vendor-select"
        />
        <Input
          placeholder="e.g. engine oil capacity"
          value={licensedQuery}
          onChange={(e) => setLicensedQuery(e.target.value)}
          onPressEnter={() => lookupLicensed(lineItemId, licensedVendor, licensedQuery)}
          data-testid="licensed-query-input"
        />
        <Button
          type="primary"
          icon={<LockOutlined />}
          loading={licensed.loading}
          disabled={!licensedQuery.trim()}
          onClick={() => lookupLicensed(lineItemId, licensedVendor, licensedQuery)}
          data-testid="licensed-lookup-btn"
        >
          Look up
        </Button>
      </Space.Compact>

      {licensed.error && (
        <Alert type="error" message={licensed.error} style={{ marginBottom: 8 }} />
      )}

      {licensed.loading && <Spin style={{ display: 'block', margin: '16px auto' }} />}

      {!licensed.loading && licensed.data && (() => {
        const result = licensed.data as AiLicensedResult;
        return (
          <Card size="small" style={{ marginTop: 8 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Tag color="blue">{result.kind}</Tag>
                <Tag color="purple">{result.source}</Tag>
                <Tag icon={<LockOutlined />} color="default">not shared</Tag>
              </Space>
              <Text strong style={{ fontSize: 15 }}>{result.value}</Text>
              {result.notes && (
                <Text type="secondary" style={{ fontSize: 12 }}>{result.notes}</Text>
              )}
              {licensedProvenance && (
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: 'block', marginTop: 4 }}
                  data-testid="licensed-provenance-note"
                >
                  {licensedProvenance}
                </Text>
              )}
            </Space>
          </Card>
        );
      })()}
    </div>
  );

  const tabItems = [
    { key: 'parts', label: 'Parts', children: PartsTab },
    { key: 'recalls', label: 'Recalls / TSBs', children: RecallsTsbsTab },
    { key: 'similar-fixes', label: 'Similar Fixes', children: SimilarFixesTab },
    { key: 'procedures', label: 'Procedures / Specs', children: ProceduresTab },
  ];

  return (
    <div
      style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '12px 16px', background: '#fafafa' }}
      data-testid="ai-lookup-panel"
    >
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
        AI Lookup — powered by Hub
      </Text>
      <Tabs size="small" defaultActiveKey="parts" items={tabItems} />

      <WarrantyEditorModal
        open={warrantyEditState.open}
        optionId={warrantyEditState.optionId}
        initialWarranty={warrantyEditState.initial}
        onClose={() => setWarrantyEditState({ open: false, optionId: null, initial: null })}
        onSaved={(w) => {
          const id = warrantyEditState.optionId;
          if (id != null) {
            setWarrantyOverrides((prev) => ({
              ...prev,
              [id]: { warranty: w, overridden: true },
            }));
          }
        }}
      />

      {/* Gap 1: Select option confirmation modal */}
      <Modal
        open={selectModal.open}
        title="Confirm Part Selection"
        okText="Select this part"
        cancelText="Cancel"
        confirmLoading={selectModal.confirming}
        onOk={confirmSelectOption}
        onCancel={() => setSelectModal({ open: false, opt: null, confirming: false, error: null })}
        data-testid="select-part-modal"
      >
        {selectModal.opt && (
          <>
            <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Brand">{selectModal.opt.brand}</Descriptions.Item>
              <Descriptions.Item label="Part #">{selectModal.opt.part_number}</Descriptions.Item>
              <Descriptions.Item label="Vendor">{selectModal.opt.vendor}</Descriptions.Item>
              {selectModal.opt.price_cents != null && (
                <Descriptions.Item label="Price">
                  ${(selectModal.opt.price_cents / 100).toFixed(2)}
                </Descriptions.Item>
              )}
              {selectModal.opt.fitment_status && (
                <Descriptions.Item label="Fitment">{selectModal.opt.fitment_status}</Descriptions.Item>
              )}
              {selectModal.opt.fitment_notes && (
                <Descriptions.Item label="Fitment notes">{selectModal.opt.fitment_notes}</Descriptions.Item>
              )}
              {selectModal.opt.stock_count != null && (
                <Descriptions.Item label="In stock">{selectModal.opt.stock_count}</Descriptions.Item>
              )}
            </Descriptions>
            {selectModal.error && (
              <Alert type="error" message={selectModal.error} showIcon />
            )}
          </>
        )}
      </Modal>

      {/* Gap 1: Part orders drawer */}
      <PartOrdersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        lineItemId={lineItemId}
        orders={partOrders}
        loading={ordersLoading}
        error={ordersError}
        onRefresh={loadPartOrders}
      />
    </div>
  );
};

export default AiLookupPanel;
